import { gql, NetworkStatus } from '@apollo/client';
import {
  PaginatedCrmActivitiesByChatIdQuery,
  PaginatedCrmActivitiesByChatIdQueryVariables,
} from '@/types';
import { useMemo } from 'react';
import { useWrappedQuery } from './use-wrapped-query';

export const PAGINATED_CRM_ACTIVITIES_BY_CHAT_ID_LIMIT = 20;
export const PAGINATED_CRM_ACTIVITIES_BY_CHAT_ID_QUERY = gql`
  query PaginatedCrmActivitiesByChatId(
    $limit: Int!
    $offset: Int
    $chatUUID: uuid!
  ) {
    crm_activities(
      where: { chat_uuid: { _eq: $chatUUID } }
      limit: $limit
      offset: $offset
      order_by: { created_at: desc }
    ) {
      created_at
      employee {
        name
        uuid
      }
      crm_activity_notes {
        content
        uuid
      }
      crm_activity_reminders {
        uuid
        remind_at
      }
      uuid
    }

    crm_activities_aggregate(where: { chat_uuid: { _eq: $chatUUID } }) {
      aggregate {
        count
      }
    }
  }
`;

/**
 * Hook used to retreive paginated crm activities
 * @typedef {Object} PaginatedCrmActivitiesHook
 * @property {Array<Crm_Activities> | undefined} data list of crm activities
 * @property {ApolloError | undefined} error error returned by server if applicable
 * @property {Function} fetchMore used to retreive additional crm activities
 * @property {boolean} fetchingMore state indicating whether or not we are loading more activities
 * @property {boolean} hasMore state indicating whether or not there are more activities to be retreived
 * @property {boolean} hasResponse state indicating whether or not a response has been received
 * @property {boolean} loading state indicating whether or not we are loading activities
 * @property {Function} refresh used to refresh or activity list results
 * @property {boolean} refreshing state indicating whether or not we are refreshing the activity list
 */

/**
 * Retreive a paginated list of crm activities by chat id
 * @param {string} chatUUID chat identifier for which we would like to retreive crm activities for
 * @returns {PaginatedCrmActivitiesHook} containing the paginated list of crm activities, network states, error states and utilities
 * to refresh and retreive more crm activities
 */
export const usePaginatedCrmActivitiesQuery = (chatUUID?: string) => {
  const {
    data,
    error,
    fetchMore,
    hasResponse,
    loading,
    networkStatus,
    refetch,
  } = useWrappedQuery<
    PaginatedCrmActivitiesByChatIdQuery,
    PaginatedCrmActivitiesByChatIdQueryVariables
  >(PAGINATED_CRM_ACTIVITIES_BY_CHAT_ID_QUERY, {
    context: {
      noBatch: true,
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    skip: !chatUUID,
    variables: {
      chatUUID: chatUUID!,
      limit: PAGINATED_CRM_ACTIVITIES_BY_CHAT_ID_LIMIT,
    },
  });

  return useMemo(() => {
    const currentCount = data?.crm_activities?.length ?? 0;
    const totalCount = data?.crm_activities_aggregate?.aggregate?.count ?? 0;
    const fetchMoreOffset = currentCount < totalCount ? currentCount : null;

    return {
      data: data?.crm_activities || [],
      error,
      fetchingMore: networkStatus === NetworkStatus.fetchMore,
      fetchMore: async () => {
        if (fetchMoreOffset !== null) {
          await fetchMore<
            PaginatedCrmActivitiesByChatIdQuery,
            PaginatedCrmActivitiesByChatIdQueryVariables
          >({
            updateQuery: (prevResult, { fetchMoreResult }) => {
              return fetchMoreResult
                ? {
                    ...prevResult,
                    crm_activities: [
                      ...prevResult.crm_activities,
                      ...fetchMoreResult.crm_activities,
                    ],
                  }
                : prevResult;
            },
            variables: {
              chatUUID,
              limit: PAGINATED_CRM_ACTIVITIES_BY_CHAT_ID_LIMIT,
              offset: fetchMoreOffset,
            },
          });
        }
      },
      hasMore: currentCount < totalCount,
      hasResponse,
      loading,
      refresh: async () => {
        await refetch();
      },
      refreshing: networkStatus === NetworkStatus.refetch,
    };
  }, [
    chatUUID,
    data,
    error,
    fetchMore,
    hasResponse,
    loading,
    networkStatus,
    refetch,
  ]);
};
